'use client'

import type { DialogAPI } from '@fukku/dialog'
import { Modal } from '@fukku/dialog/Modal'
import { NonModal } from '@fukku/dialog/NonModal'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import dynamic from 'next/dynamic'
import { useEffect, useRef } from 'react'
import { useResponsiveBehavior } from 'responsive/hooks/useResponsiveBehavior/useResponsiveBehavior'
import { cx } from 'utils/cx'
import { dataTestIds } from '../../../constants/dataTestIds'
import { useChatbotDialog } from '../../../hooks/useChatbotDialog'
import styles from './ChatDialog.module.scss'

const DynamicChatDialogWrapper = dynamic(() =>
	import('./ChatDialogWrapper/ChatDialogWrapper').then(
		(mod) => mod.ChatDialogWrapper
	)
)

const DynamicChatbotProvider = dynamic(() =>
	import('../../../contexts/ChatbotProvider').then((mod) => mod.ChatbotProvider)
)

export function ChatDialog() {
	const { isDialogOpen, isDialogMinimized } = useChatbotDialog()

	const { t } = useLabels()

	const dialogRef = useRef<DialogAPI>(null)

	const ResponsiveDialog = useResponsiveBehavior({
		default: NonModal,
		small: Modal,
	})

	const isModal = useResponsiveBehavior<boolean>({
		default: false,
		small: true,
	})

	useEffect(() => {
		if (isDialogOpen) {
			dialogRef.current?.show()
		}
	}, [ResponsiveDialog, isDialogOpen])

	return isDialogOpen || isDialogMinimized ? (
		<DynamicChatbotProvider>
			<ResponsiveDialog
				location={{
					bottom: '0px',
					right: '0px',
				}}
				className={cx(!isModal && styles.chatDialog)}
				title={t('assistant.header.title')}
				ref={dialogRef}
				preventClose
				isFullScreen
				data-testid={dataTestIds.chatbotContainer}
			>
				{isDialogOpen && (
					<DynamicChatDialogWrapper dialogRef={dialogRef} isModal={isModal} />
				)}
			</ResponsiveDialog>
		</DynamicChatbotProvider>
	) : null
}
